import React, { useEffect, useState } from 'react';
import { Settings, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { whatsappAPI } from '../services/api';
import { ModernButton, ModernCard, ModernLoadingSpinner } from '../components/ui/ModernComponents';

const DEFAULTS = {
  green_api_id_instance: '',
  green_api_token_instance: '',
  green_api_media_url: 'https://api.green-api.com',
  public_base_url: '',
  whatsapp_keep_uploads_after_send: false,
  order_validation_template: '',
  message_template_confirmation: '',
  message_template_annulation: '',
  message_template_en_cours: '',
  message_template_livree: '',
  message_template_promotion: '',
  message_template_nouvel_arrivage: '',
};

export default function WhatsAppConfig() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await whatsappAPI.getConfig();
        setForm({ ...DEFAULTS, ...(data || {}) });
      } catch (e) {
        toast.error(e.response?.data?.message || 'Erreur chargement configuration WhatsApp');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await whatsappAPI.updateConfig(form);
      toast.success('Configuration WhatsApp enregistrée');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erreur sauvegarde configuration WhatsApp');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const inputClass =
    'w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none';
  const labelClass = 'block text-sm font-medium text-neutral-700 mb-1';

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <Settings className="h-7 w-7 text-primary-600" />
          Configuration WhatsApp
        </h1>
        <p className="text-neutral-600 mt-1">
          Configurez l&apos;instance Green API, les URLs et les modèles de messages WhatsApp depuis cette page.
        </p>
      </div>

      <ModernCard className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Green API</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelClass}>ID Instance</label>
            <input
              className={inputClass}
              value={form.green_api_id_instance || ''}
              onChange={(e) => setForm((f) => ({ ...f, green_api_id_instance: e.target.value }))}
              placeholder="ex: 7107544173"
            />
          </div>
          <div>
            <label className={labelClass}>Token Instance</label>
            <input
              className={inputClass}
              value={form.green_api_token_instance || ''}
              onChange={(e) => setForm((f) => ({ ...f, green_api_token_instance: e.target.value }))}
              placeholder="api token instance"
            />
          </div>
          <div>
            <label className={labelClass}>Media URL</label>
            <input
              className={inputClass}
              value={form.green_api_media_url || ''}
              onChange={(e) => setForm((f) => ({ ...f, green_api_media_url: e.target.value }))}
              placeholder="https://api.green-api.com"
            />
          </div>
          <div>
            <label className={labelClass}>Public Base URL (optionnel)</label>
            <input
              className={inputClass}
              value={form.public_base_url || ''}
              onChange={(e) => setForm((f) => ({ ...f, public_base_url: e.target.value }))}
              placeholder="https://crmapi.epoptique.ma"
            />
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={!!form.whatsapp_keep_uploads_after_send}
            onChange={(e) =>
              setForm((f) => ({ ...f, whatsapp_keep_uploads_after_send: e.target.checked }))
            }
          />
          Conserver les fichiers uploadés sur le serveur après envoi réussi
        </label>
      </ModernCard>

      <ModernCard className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-neutral-900">Templates messages WhatsApp</h2>
        <p className="text-xs text-neutral-500">
          Variables pour commandes: <code>{'{client_name}'}</code>, <code>{'{order_id}'}</code>,{' '}
          <code>{'{status}'}</code>, <code>{'{order_date}'}</code>, <code>{'{total}'}</code>,{' '}
          <code>{'{total_paid}'}</code>, <code>{'{items}'}</code>.
        </p>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {[
          ['message_template_confirmation', 'Confirmation commande'],
          ['message_template_annulation', 'Annulation commande'],
          ['message_template_en_cours', 'Commande en cours'],
          ['message_template_livree', 'Commande livrée / prête'],
          ['message_template_promotion', 'Promotion'],
          ['message_template_nouvel_arrivage', 'Nouvel arrivage'],
          ['order_validation_template', 'Validation commande (ventes/commandes)'],
        ].map(([key, label]) => (
          <div key={key}>
            <label className={labelClass}>{label}</label>
            <textarea
              className={`${inputClass} min-h-[120px]`}
              value={form[key] || ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              placeholder={`Template ${label}`}
            />
          </div>
        ))}
        </div>
      </ModernCard>

      <div className="sticky bottom-0 z-10 border-t border-neutral-200 bg-white/95 backdrop-blur px-1 py-3 -mx-1 flex justify-end">
        <ModernButton
          variant="primary"
          icon={<Save className="w-4 h-4" />}
          onClick={saveConfig}
          disabled={saving}
        >
          {saving ? 'Sauvegarde…' : 'Enregistrer la configuration'}
        </ModernButton>
      </div>
    </div>
  );
}

